import { castArray, isEmpty } from "lodash";
import { logger } from "./logging/Logger";

export const EXCLUDE_TAGS: string[] = ["_apptuit_named_query", "__apptuit__metric", "__apptuit__mq", "__apptuit_ts"];
export const QUANTILE_TAG = "quantile";
export const NAME_TAG = "__name__";

export const GENERIC_LOGIN_DOMAIN = "account";

export const shouldExcludeTag = (tag: string, excludeNameTag?: boolean) => {
  const excludeRegEx = /^(__apptuit__|__inception__|_inception_).*$/g;
  const excludeTags = [...EXCLUDE_TAGS];
  excludeNameTag && excludeTags.push(NAME_TAG);

  return !isEmpty(tag) && (excludeTags.includes(tag) || excludeRegEx.test(tag));
};
export function extractPythonCodes(markdown: string): string[] {
  if (!markdown) {
    return [];
  }

  const pythonBlockRegex = /```python\s+([\s\S]*?)\s+```/g;
  const results: string[] = [];
  let match;

  while ((match = pythonBlockRegex.exec(markdown)) !== null) {
    results.push((match[1] || "").trim());
  }

  return results;
}

export function hasCodeBlock(markdown: string): boolean {
  if (!markdown) {
    return false;
  }

  const codeBlockRegex = /```python/;

  return codeBlockRegex.test(markdown);
}

export const DataTypeWithDefault = {
  /**
   * Takes a input and if its not empty return it else fallsback to default value
   * @param val
   * @param defaultVal
   */
  getStr: function (val: string | number, defaultVal: string = null): string {
    return !isEmpty(val) ? val.toString() : defaultVal;
  },

  getStrArr: function (val: string | string[], defaultVal: string[] = []): string[] {
    return !isEmpty(val) ? (castArray(val) as string[]) : defaultVal;
  },

  getTypedArr: function <T>(val: T | T[], defaultVal: T[] = []): T[] {
    return !isEmpty(val) ? (castArray(val) as T[]) : defaultVal;
  }
};

export const getSafeNumber = (num: number, safeDefault = 0) => {
  const safeNum = parseInt(num.toString(), 10);
  return isNaN(safeNum) ? safeDefault : safeNum;
};

export const getChangePercent = (value: number, timeShiftValue: number) => {
  const changeValue = Math.abs(timeShiftValue - value);
  const changePercent = (changeValue / timeShiftValue) * 100;

  const changePercentStr = isNaN(changePercent) ? "" : changePercent.toFixed(2);

  return {
    changePercent,
    changePercentStr
  };
};

function jsonToCsv(json: Array<Record<string, any>>) {
  const keys = Object.keys(json[0]);
  const csvRows = [];

  // Add headers
  csvRows.push(keys.join(","));

  // Add rows
  json.forEach(obj => {
    const values = keys.map(key => {
      if (obj[key]) {
        const escapedValue = obj[key].toString().replace(/"/g, '\\"');
        return `"${escapedValue}"`; // Enclose values in double quotes
      }
      return "";
    });
    csvRows.push(values.join(","));
  });

  return csvRows.join("\n");
}

export const downloadCSVFromJsonRecords = (jsonList: Array<Record<string, any>>, fileName?: string) => {
  const csvString = jsonToCsv(jsonList);
  const blob = new Blob([csvString], { type: "text/csv" });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName ? `${fileName}.csv` : "data.csv";
  a.click();

  URL.revokeObjectURL(url);
};

export const updateSvgColor = (svgString: string, newColor: string) => {
  // Parse the SVG string into a DOM object
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgString, "image/svg+xml");

  // Get all path elements (you can also target other elements like rect, circle, etc.)
  const paths = svgDoc.querySelectorAll("path");

  // Update the color of each path element
  paths.forEach(path => {
    path.setAttribute("fill", newColor);
  });

  // Serialize the updated SVG back to a string
  const serializer = new XMLSerializer();
  const updatedSvgString = serializer.serializeToString(svgDoc);

  return updatedSvgString;
};

/**
 * Recursively processes an object to convert function strings to actual JavaScript functions.
 * This is needed because JSON cannot contain functions, so the backend sends them as strings.
 *
 * @param obj - The object to process (can be any value)
 * @returns The processed object with function strings converted to functions
 */
export function parseHighchartsOptionsStrings(obj: any): any {
  if (obj === null || obj === undefined) {
    return {};
  }

  // If it's already a function, return it as-is
  if (typeof obj === "function") {
    return obj;
  }

  // If it's a string that looks like a function, convert it
  if (typeof obj === "string") {
    const trimmed = obj.trim();
    // Check if it looks like a function (starts with "function" or is an arrow function)
    if (
      trimmed.startsWith("function") ||
      (trimmed.startsWith("(") && trimmed.includes("=>")) ||
      trimmed.startsWith("async")
    ) {
      try {
        // Use Function constructor to safely convert string to function
        return new Function(`return ${trimmed}`)();
      } catch (error) {
        logger.warn(
          "parseFunctionStrings",
          `Failed to parse function string, removing property to prevent chart breakage: ${trimmed}`,
          error
        );
        // Return undefined to remove the property so chart can render without breaking
        return null;
      }
    }
    return obj;
  }

  // If it's an array, process each element and filter out failed parses
  if (Array.isArray(obj)) {
    return obj.map(item => parseHighchartsOptionsStrings(item)).filter(item => item !== null);
  }

  // If it's an object, process each property
  if (typeof obj === "object") {
    const processed: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const parsedValue = parseHighchartsOptionsStrings(obj[key]);
        // Only include the property if parsing didn't fail (undefined means parsing failed)
        if (parsedValue !== null) {
          processed[key] = parsedValue;
        }
      }
    }
    return processed;
  }

  // For primitives (number, boolean, etc.), return as-is
  return obj;
}
